import { type ComponentPropsWithoutRef } from "react";
import PillarForm from "shared/components/pillar-form/PillarForm";
import { getReportAccessConsumerLinks } from "shared/components/report-access/reportAccessLinks";
import { useCustomRouter } from "shared/context/CustomRouterContext";
import { useSocietySession } from "shared/context/SocietySessionContext";
import {
  type ReportAccessConsumer,
  ReportAccessConsumerType,
} from "shared/report-access/reportAccessTypes";

const consumerTypeLabel: Record<ReportAccessConsumerType, string> = {
  [ReportAccessConsumerType.appRoute]: "App routes",
  [ReportAccessConsumerType.directory]: "Directories",
  [ReportAccessConsumerType.dashboardModule]: "Dashboard modules",
  [ReportAccessConsumerType.adminDashboardPage]: "Admin dashboard pages",
  [ReportAccessConsumerType.conference]: "Conferences",
  [ReportAccessConsumerType.form]: "Forms",
  [ReportAccessConsumerType.defaultReport]: "Default admin reports",
};

const describeConsumer = (consumer: ReportAccessConsumer): string => {
  if (consumer.type === ReportAccessConsumerType.appRoute) {
    const via =
      consumer.directoryId === undefined
        ? consumer.paramKey
        : `through directory ${consumer.directoryId}`;
    return `${consumer.label} (${consumer.url}, ${via})`;
  }
  return consumer.detail
    ? `${consumer.label} (${consumer.detail})`
    : consumer.label;
};

const consumerKey = (consumer: ReportAccessConsumer): string =>
  consumer.type === ReportAccessConsumerType.appRoute
    ? `${consumer.type}-${consumer.appRouteConfigId}-${consumer.paramKey}-${consumer.directoryId ?? ""}`
    : `${consumer.type}-${consumer.id}-${consumer.detail ?? ""}`;

type ReportAccessUsedByPanelProps = {
  /** Everything that uses the report */
  consumers: ReportAccessConsumer[];
  /** data-testid prefix */
  testid: string;
} & ComponentPropsWithoutRef<"div">;

/** Lists what uses a report, grouped by kind, with links to each */
const ReportAccessUsedByPanel = ({
  consumers,
  testid,
  className,
  ...props
}: ReportAccessUsedByPanelProps) => {
  const router = useCustomRouter();
  const { societyId } = useSocietySession();

  const groups = Object.values(ReportAccessConsumerType)
    .map((type) => ({
      type,
      consumers: consumers.filter((consumer) => consumer.type === type),
    }))
    .filter((group) => group.consumers.length > 0);

  return (
    <div
      className={`rounded border border-neutral-light-600 p-1.5 ${className ?? ""}`}
      data-testid={testid}
      {...props}
    >
      <h2 className="mb-1 font-semibold">Used by</h2>
      {groups.length === 0 ? (
        <p className="text-sm" data-testid={`${testid}-empty`}>
          Nothing references this report.
        </p>
      ) : (
        <div className="flex flex-col gap-1">
          {groups.map((group) => (
            <div key={group.type} data-testid={`${testid}-${group.type}`}>
              <p className="text-sm font-semibold">
                {consumerTypeLabel[group.type]}
              </p>
              <ul className="flex flex-col gap-0.5">
                {group.consumers.map((consumer) => (
                  <li
                    key={consumerKey(consumer)}
                    className="flex flex-wrap items-center gap-1 text-sm"
                  >
                    <span>{describeConsumer(consumer)}</span>
                    {getReportAccessConsumerLinks({ societyId, consumer }).map(
                      (link) => (
                        <PillarForm.Button
                          key={link.key}
                          label={link.label}
                          className="button-small-neutral-filled"
                          testid={`${testid}-link-${link.key}`}
                          onClick={() => router.push(link.href)}
                        />
                      ),
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReportAccessUsedByPanel;
